import { 
  CLIENT_ENDPOINTS, 
  WORKFLOW_ENDPOINTS,
  MODULE1_ENDPOINTS,
  MODULE2_ENDPOINTS,
  MODULE3_ENDPOINTS,
  MODULE4_ENDPOINTS
} from '../api/endpointMap';
import { versionedApiClient, ApiVersion } from '../api/versionedClient';

/**
 * Helper class to analyze the status of API endpoints during development
 */
export class EndpointAnalyzer {
  /**
   * Check if all v1 endpoints have db equivalents
   * @returns Promise<{missingEndpoints: string[], totalEndpoints: number}>
   */
  static async checkMissingDbEndpoints(): Promise<{missingEndpoints: string[], totalEndpoints: number}> {
    // Combine all endpoint collections
    const allEndpoints = {
      ...CLIENT_ENDPOINTS,
      ...WORKFLOW_ENDPOINTS,
      ...MODULE1_ENDPOINTS,
      ...MODULE2_ENDPOINTS,
      ...MODULE3_ENDPOINTS,
      ...MODULE4_ENDPOINTS
    };

    const endpointResults: {endpoint: string, v1: boolean, db: boolean}[] = [];
    const missingEndpoints: string[] = [];
    
    // Suppress console errors during this check
    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;
    
    try {
      // Temporarily override console.error for this operation
      console.error = function(...args: any[]) {
        // Only log if it's not an axios error or 404
        if (!args[0]?.toString().includes('Error: Request failed') && 
            !args[0]?.toString().includes('404')) {
          originalConsoleError.apply(console, args);
        }
      };
      
      console.warn = function(...args: any[]) {
        // Only log if it's not related to missing endpoints
        if (!args[0]?.toString().includes('MISSING') && 
            !args[0]?.toString().includes('not found')) {
          originalConsoleWarn.apply(console, args);
        }
      };
      
      // Check each endpoint for both v1 and db versions
      for (const [key, endpoint] of Object.entries(allEndpoints)) {
        // Skip if not an object with versions
        if (typeof endpoint !== 'object') continue;
        
        const v1Endpoint = (endpoint as any).v1;
        const dbEndpoint = (endpoint as any).db;
        
        if (!v1Endpoint) continue; // Skip if no v1 version
        
        let v1Available = false;
        let dbAvailable = false;
        
        // Check v1 endpoint availability
        try {
          if (!v1Endpoint.includes('{id}')) {
            await versionedApiClient.get(v1Endpoint, {}, {}, 'v1');
            v1Available = true;
          } else {
            // For endpoints with parameters, assume they're available
            v1Available = true;
          }
        } catch (error) {
          // If it's not a 404, assume it might be available with proper params
          if ((error as any).response?.status !== 404) {
            v1Available = true;
          }
        }
        
        // Check db endpoint availability
        if (dbEndpoint) {
          try {
            if (!dbEndpoint.includes('{id}')) {
              await versionedApiClient.get(dbEndpoint, {}, {}, 'db');
              dbAvailable = true;
            } else {
              // For endpoints with parameters, we need to test manually
              dbAvailable = false;
            }
          } catch (error) {
            // If it's a 404, it's definitely missing
            if ((error as any).response?.status === 404) {
              dbAvailable = false;
            } else {
              // Other errors might indicate it exists but needs parameters
              dbAvailable = true;
            }
          }
        }
        
        endpointResults.push({
          endpoint: key,
          v1: v1Available,
          db: dbAvailable
        });
        
        // If v1 is available but db is not, add to missing endpoints
        if (v1Available && !dbAvailable && dbEndpoint) {
          missingEndpoints.push(dbEndpoint);
        }
      }
    } finally {
      // Restore original console methods
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
    }
    
    // Log a single summary instead of detailed logs
    console.log(`Endpoint Analysis complete: ${missingEndpoints.length} missing endpoints out of ${endpointResults.length} total`);
    
    return {
      missingEndpoints,
      totalEndpoints: endpointResults.length
    };
  }
  
  /**
   * Clear the missing endpoints tracker
   */
  static clearMissingEndpoints(): void {
    if (window.missingDbEndpoints) {
      window.missingDbEndpoints = [];
    }
  }
  
  /**
   * Get the current list of missing endpoints
   */
  static getMissingEndpoints(): { endpoint: string, version: ApiVersion, timestamp: string }[] {
    return window.missingDbEndpoints || [];
  }
}

export default EndpointAnalyzer; 